// src/utils/renderCustomBaslik.js
// Ozel sablon render + temizleme

import * as THREE from 'three';
import DxfParser from 'dxf-parser';
import { getHatchFromDXF, getEntityTransparencyMap, getAttribsFromDXF, getLayersFromDXF, getOleFramesFromDXF } from '../modules/dxf';
import { dxfDrawCore } from '../modules/dxf';
import { resolveVariablePath } from '../config/baslikConfig/variableCatalog';
import { buildMarkaEntities } from '../modules/documents';

const CUSTOM_BASLIK_TAG = 'isCustomBaslik';

// DXF parse sonucunu template id bazlı cache'leyerek tekrar parse'ı (12 MB string +
// HATCH/OLE/ATTRIB/layer çıkarımları) önle. Aynı şablonu her layout re-run'da yeniden
// parse etmek bellek baskısı oluşturuyordu. WeakMap kullanmıyoruz — template objesi
// reduce'ta yenilenebiliyor; id sabit kalıyor.
const PARSED_TEMPLATE_CACHE = new Map();
const MAX_CACHE_ENTRIES = 4;

function parseTemplateOnce(template) {
  const key = template.id || template.docId;
  if (key && PARSED_TEMPLATE_CACHE.has(key)) {
    const entry = PARSED_TEMPLATE_CACHE.get(key);
    if (entry.dxfRawText === template.dxfRawText) return entry.data;
  }

  const parser = new DxfParser();
  let dxf;
  try {
    dxf = parser.parseSync(template.dxfRawText);
  } catch (err) {
    console.error('[parseTemplateOnce] DXF parse hatasi:', err);
    return null;
  }

  const dxfLines = template.dxfRawText.split('\n').map(line => line.trim());

  const hatchEntities = getHatchFromDXF(dxfLines);
  if (Array.isArray(hatchEntities.hatch)) {
    dxf.entities = [...dxf.entities, ...hatchEntities.hatch];
  }

  const transparencyMap = getEntityTransparencyMap(dxfLines);
  dxf.entities = dxf.entities.map(entity => {
    const handleKey = entity.handle?.toUpperCase();
    if (handleKey && transparencyMap[handleKey] !== undefined) {
      return { ...entity, transparency: transparencyMap[handleKey] };
    }
    return entity;
  });

  const oleEntities = getOleFramesFromDXF(dxfLines);
  if (oleEntities.length > 0) {
    dxf.entities = [...dxf.entities, ...oleEntities];
  }

  const attribsByInsert = getAttribsFromDXF(dxfLines);
  dxf.entities = dxf.entities.map(entity => {
    if (entity.type === 'INSERT') {
      const handleKey = entity.handle?.toUpperCase();
      if (handleKey && attribsByInsert[handleKey]) {
        return { ...entity, attribs: attribsByInsert[handleKey] };
      }
    }
    return entity;
  });

  const parsedLayers = dxf.tables?.layer?.layers;
  let layerArray = Array.isArray(parsedLayers)
    ? parsedLayers
    : parsedLayers ? Object.values(parsedLayers) : [];
  const enhancedLayers = getLayersFromDXF(dxfLines);
  layerArray = layerArray.map(layer => {
    const enhanced = enhancedLayers[layer.name];
    if (enhanced) {
      return {
        ...layer,
        onOff: enhanced.onOff,
        locked: enhanced.locked,
        frozen: enhanced.frozen,
        lineType: enhanced.lineType,
        lineWeight: enhanced.lineWeight,
        transparency: enhanced.transparency ?? 0,
      };
    }
    return layer;
  });

  const data = { dxf, layers: layerArray };

  if (key) {
    // Cache'i sınırlı tut — eski entry'leri at
    if (PARSED_TEMPLATE_CACHE.size >= MAX_CACHE_ENTRIES) {
      const firstKey = PARSED_TEMPLATE_CACHE.keys().next().value;
      PARSED_TEMPLATE_CACHE.delete(firstKey);
    }
    PARSED_TEMPLATE_CACHE.set(key, { dxfRawText: template.dxfRawText, data });
  }
  return data;
}

/**
 * Şablonun DXF rawText'inden bir THREE.Group oluşturur.
 *   - cache'li parse (HATCH/OLE/ATTRIB/layer çıkarımları tek seferde)
 *   - bindings → resolved values (TEXT/MTEXT)
 *   - dxfDrawCore(target='group') → grup
 *   - sol-üst köşe (0,0)'a normalize edilir (top at y=0, bottom at y=-h)
 * Dönen grup CUSTOM_BASLIK_TAG ile etiketli ve bbox bilgisi userData'da bulunur.
 */
export function buildBaslikTemplateGroup(template, reduxState) {
  if (!template || !template.dxfRawText) return null;

  const parsed = parseTemplateOnce(template);
  if (!parsed) return null;
  const { dxf, layers: layerArray } = parsed;

  // Binding → resolved values (her render'da yapılmalı — reduxState değişebilir).
  // ÖNEMLİ: dxf.entities MUTATE ETMİYORUZ — cache'lenmiş objedir, sonraki çağrılarda bozulur.
  const bindings = template.bindings || {};
  let textIdx = 0;
  let mtextIdx = 0;
  const entitiesForRender = dxf.entities.map(entity => {
    let entityKey = null;
    if (entity.type === 'TEXT') entityKey = `text_${textIdx++}`;
    else if (entity.type === 'MTEXT') entityKey = `mtext_${mtextIdx++}`;
    if (entityKey && bindings[entityKey]) {
      const resolved = resolveVariablePath(reduxState, bindings[entityKey].variablePath);
      return { ...entity, text: resolved || entity.text };
    }
    return entity;
  });

  // ── Marka (firma) alanı ayarlarını uygula ──
  // CAD Oluşturucu'da kaydedilen marka: { ole, native, circle, square, data }.
  // ole=false → OLE2FRAME gizlenir; native=true → firma bilgisi native geometriyle
  // OLE hücresine (sınırlarından tespit) yazılır. OLE yoksa buildMarkaEntities varsayılan.
  let finalEntities = entitiesForRender;
  const marka = template.marka;
  if (marka && (marka.native || marka.ole === false)) {
    const oleEnt = entitiesForRender.find(e => e.type === 'OLE2FRAME');
    if (marka.ole === false) {
      finalEntities = finalEntities.filter(e => e.type !== 'OLE2FRAME');
    }
    if (marka.native && marka.data) {
      const shapeOpts = { circleMode: marka.circle ?? 2, squareMode: marka.square ?? 0 };
      let mo = { drawFrame: false, ...shapeOpts };
      if (oleEnt?.upperLeft && oleEnt?.lowerRight) {
        const ul = oleEnt.upperLeft, lr = oleEnt.lowerRight;
        const x0 = Math.min(ul.x, lr.x), x1 = Math.max(ul.x, lr.x);
        const y0 = Math.min(ul.y, lr.y), y1 = Math.max(ul.y, lr.y);
        if (x1 > x0 && y1 > y0) {
          mo = { drawFrame: false, originX: x0, originY: y0, width: x1 - x0, height: y1 - y0, ...shapeOpts };
        }
      }
      const markaEnts = buildMarkaEntities(marka.data, mo);
      if (markaEnts.length) finalEntities = [...finalEntities, ...markaEnts];
    }
  }

  // target='group' ile çiz — dxfDrawCore scene'e bir şey eklemez
  const stubScene = new THREE.Group();
  const drawingGroup = dxfDrawCore(stubScene, finalEntities, dxf.blocks || {}, 'group', layerArray);
  if (!drawingGroup) return null;

  // Bbox normalize — sol-üst köşe (0,0); sağ-alt (w, -h)
  // (Layout konvansiyonu: dokümanlar top at y=0, bottom at y=-h.)
  const bbox = new THREE.Box3().setFromObject(drawingGroup);
  if (bbox.isEmpty()) return null;
  const size = bbox.getSize(new THREE.Vector3());
  drawingGroup.position.set(-bbox.min.x, -bbox.max.y, 0);

  drawingGroup.userData = drawingGroup.userData || {};
  drawingGroup.userData[CUSTOM_BASLIK_TAG] = true;
  drawingGroup.userData.bbox = { width: size.x, height: size.y };
  markAsCustomBaslik(drawingGroup);

  return drawingGroup;
}

/**
 * Ozel sablonu ana sahneye render eder (standalone kullanim — BaslikPanel testi vb.).
 * Layout entegrasyonu icin doğrudan buildBaslikTemplateGroup'u kullanın.
 */
export function renderCustomBaslik(scene, template, reduxState) {
  if (!scene) return;
  clearCustomBaslik(scene);
  const group = buildBaslikTemplateGroup(template, reduxState);
  if (group) scene.add(group);
}

/**
 * Ozel baslik isaretli nesneleri traverse + dispose + remove
 */
export function clearCustomBaslik(scene) {
  if (!scene) return;

  const toRemove = [];
  scene.traverse((obj) => {
    if (obj.userData && obj.userData[CUSTOM_BASLIK_TAG]) {
      toRemove.push(obj);
    }
  });

  // En ust seviye nesneleri bul (parent'i scene olan)
  const topLevel = toRemove.filter(obj => obj.parent === scene);

  topLevel.forEach((obj) => {
    // Tum alt nesneleri dispose et
    obj.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(m => {
            if (m.map) m.map.dispose();
            m.dispose();
          });
        } else {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      }
    });
    scene.remove(obj);
  });
}

/**
 * Bir nesne ve tum alt nesnelerini isaretle
 */
function markAsCustomBaslik(obj) {
  if (!obj) return;
  obj.userData = obj.userData || {};
  obj.userData[CUSTOM_BASLIK_TAG] = true;
  if (obj.children) {
    obj.children.forEach(child => markAsCustomBaslik(child));
  }
}
